'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Switch as SwitchPrimitives } from 'radix-ui';

import { cn } from '../../lib/utils';

const switchRootVariants = cva(
  [
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors focus-ring',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'data-[state=unchecked]:bg-quaternary',
    'data-[state=unchecked]:hover:bg-tertiary',
    'data-[state=checked]:bg-brand',
    'data-[state=checked]:hover:bg-brand-hover',
  ].join(' '),
  {
    variants: {
      size: {
        small: 'h-4 w-7',
        medium: 'h-5 w-9',
        large: 'h-6 w-11',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
);

const switchThumbVariants = cva(
  [
    'pointer-events-none block rounded-full bg-canvas',
    'transition-transform',
    'data-[state=unchecked]:translate-x-0',
    'data-[state=checked]:bg-inverse-fg',
  ].join(' '),
  {
    variants: {
      size: {
        // p-0.5 = 2px equal inset; thumb fills remaining height
        small: 'size-3 data-[state=checked]:translate-x-3',
        medium: 'size-4 data-[state=checked]:translate-x-4',
        large: 'size-5 data-[state=checked]:translate-x-5',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
);

export interface SwitchProps
  extends
    React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchRootVariants> {}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, size, ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(switchRootVariants({ size }), className)}
      tabIndex={0}
      data-slot="switch"
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb className={cn(switchThumbVariants({ size }))} />
    </SwitchPrimitives.Root>
  ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
